import React, { useEffect, useState } from "react";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SiteByIdDTO, deleteEstimateById, getSiteById } from "api";
import { EsgDeleteIcon, EsgNotesIcon, PlusIcon } from "assets";
import {
  COLORS,
  CustomMaterialTable,
  DeleteDialog,
  SwitchToActivation,
  useDialogControls,
} from "components";
import { QUERY_KEYS } from "consts";
import { useDeactivatedEstimatesBySiteId, useNotify } from "hooks";
import { useUserRole } from "recoils";
import { AllowedFormValues } from "types";

import { estimatesColumns } from "./consts";
import { EstimatesModal } from "./components";

export const ParameterBasedEstimation = ({
  siteData,
  allowedValues,
  onDisableEstimate,
  onEstimatesDataUpdate,
}: {
  siteData: SiteByIdDTO;
  allowedValues: AllowedFormValues;
  onDisableEstimate: (id: number | undefined) => void;
  onEstimatesDataUpdate: (siteData: SiteByIdDTO) => void;
}) => {
  const userRole = useUserRole();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const deleteDialogControls = useDialogControls();
  const permanentDeleteDialogControls = useDialogControls();

  const estimatesRecords = siteData?.estimates || [];
  const {
    data: deactivatedEstimates,
    isLoading: isDeactivatedEstimatesLoading,
    refetch: refetchDeactivatedEstimates,
  } = useDeactivatedEstimatesBySiteId(siteData?.id ?? null);

  const [addEstimatesModalOpen, setAddEstimatesModalOpen] =
    useState<boolean>(false);
  const [EditEstimate, setEditEstimate] = useState<any | undefined>();
  const [EditEstimateOpen, setEditEstimateOpen] = useState<boolean>(false);
  const [editEstimateNotesOpen, setEditEstimateNotesOpen] =
    useState<boolean>(false);
  const [isEstimateModalOpen, setEstimateModalOpen] = useState<boolean>(false);
  const [deleteEstimateId, setDeleteEstimateId] = useState<number | undefined>(
    undefined,
  );
  const [permanentDeleteEstimateId, setPermanentDeleteEstimateId] = useState<
    number | undefined
  >(undefined);
  const [deActivatedEstimatesList, setDeActivatedEstimatesList] = useState<any>(
    [],
  );
  const [isEditNotes, setIsEditNotes] = useState<boolean>(false);

  const { mutateAsync: deleteEstimateRequest } =
    useMutation(deleteEstimateById);

  const setEstimateToDelete = (id: number | undefined) => {
    deleteDialogControls.open();
    if (id) {
      setDeleteEstimateId(id);
    }
  };

  const openPermanentDeleteDialog = (id: number) => {
    setPermanentDeleteEstimateId(id);
    permanentDeleteDialogControls.open();
  };

  const handlePermanentDeleteEstimate = async () => {
    if (!permanentDeleteEstimateId) return;
    try {
      await deleteEstimateRequest(permanentDeleteEstimateId, {
        onSuccess: async () => {
          notify.success("Estimate deleted successfully.");
          permanentDeleteDialogControls.close();
          queryClient.invalidateQueries([
            QUERY_KEYS.ESTIMATES,
            QUERY_KEYS.SITES,
            siteData?.id,
          ]);
          if (siteData?.id) {
            const updatedSite = await getSiteById(siteData.id);
            onEstimatesDataUpdate(updatedSite);
          }
        },
      });
    } catch (error: any) {
      notify.error(
        error?.response?.data && typeof error?.response?.data === "string"
          ? error?.response?.data
          : "An error occurred while deleting the estimate.",
      );
      permanentDeleteDialogControls.close();
    }
  };

  const handleCloseEstimatesModal = () => {
    const isAddEstiamtesModalOpen = addEstimatesModalOpen;
    const isEditEstimateOpen = EditEstimateOpen;

    if (isAddEstiamtesModalOpen) {
      setAddEstimatesModalOpen(false);
    }
    if (isEditEstimateOpen) {
      setEditEstimateOpen(false);
    }
    if (editEstimateNotesOpen) {
      setEditEstimateNotesOpen(false);
      setIsEditNotes(false);
    }
    if (isEstimateModalOpen) {
      setEstimateModalOpen(false);
      setIsEditNotes(false);
    }

    setEditEstimate(undefined);
    refetchDeactivatedEstimates();
  };

  const handleEditEstimates = (rowData: any) => {
    setEditEstimateOpen(true);
    setEditEstimate(rowData);
  };

  const handleEditEstimatesNotes = (rowData: any) => {
    setEditEstimateNotesOpen(true);
    setIsEditNotes(true);
    setEditEstimate(rowData);
  };

  const deleteEstimate = (id: number | undefined) => {
    onDisableEstimate(id);
    deleteDialogControls.close();
  };

  useEffect(() => {
    if (deactivatedEstimates) {
      setDeActivatedEstimatesList(deactivatedEstimates);
    }
  }, [deactivatedEstimates]);

  return (
    <Box sx={{ px: 3, mt: 1 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          mb: 1,
        }}
      >
        <p style={{ fontSize: "16px", fontWeight: "500" }}>
          Parameter Based Estimations
        </p>
        <Button
          sx={{ borderRadius: "8px", width: "200px", height: "36px" }}
          variant="contained"
          onClick={() => setAddEstimatesModalOpen(true)}
          startIcon={<PlusIcon />}
          disabled={userRole.isAuditor}
        >
          Add Estimates
        </Button>
      </Box>
      <Box>
        <CustomMaterialTable
          data={[...estimatesRecords, ...deActivatedEstimatesList]}
          columns={estimatesColumns}
          enableTopToolbar={false}
          enableRowVirtualization={false}
          enablePagination={false}
          state={{ isLoading: isDeactivatedEstimatesLoading }}
          initialState={{ density: "compact" }}
          enableRowActions
          rowClickable
          muiTableContainerProps={{
            sx: {
              width: "calc(100vw - 150px)",
              "& tbody tr td": {
                paddingY: "0px",
              },
              "& thead tr th:first-of-type": {
                borderRight: "1px solid #e0e0e0",
                "& > div": { pl: "8px" },
              },
              "& tbody tr td:first-of-type": {
                borderRight: "1px solid #e0e0e0",
              },
            },
          }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () => {
              if (row.original.enabled) {
                handleEditEstimates(row.original);
              }
            },
          })}
          renderRowActions={({ row }) => (
            <Box
              sx={{
                height: "45px",
                display: "flex",
                alignItems: "center",
                pl: "5px",
              }}
            >
              <SwitchToActivation
                enabled={row.original.enabled}
                textOn="Deactivate"
                textOff="De-Activated"
                onClick={(e) => {
                  e.stopPropagation();
                  if (row.original?.enabled) {
                    setEstimateToDelete(row.original.id as number);
                  }
                }}
              />
              <Tooltip arrow placement="top" title="Notes">
                <IconButton
                  sx={{ width: "35px", height: "35px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditEstimatesNotes(row.original);
                  }}
                >
                  <EsgNotesIcon
                    color={
                      row.original.notes?.length > 0 ? COLORS.Romance : "grey"
                    }
                  />
                </IconButton>
              </Tooltip>
              <Tooltip arrow placement="top" title="Delete">
                <IconButton
                  sx={{ width: "35px", height: "35px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openPermanentDeleteDialog(row.original.id as number);
                  }}
                >
                  <EsgDeleteIcon color="#EF2E14" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        />
      </Box>
      <EstimatesModal
        isOpen={
          addEstimatesModalOpen ||
          EditEstimateOpen ||
          isEstimateModalOpen ||
          editEstimateNotesOpen
        }
        onClose={handleCloseEstimatesModal}
        allowedValues={allowedValues}
        siteData={siteData || ({} as SiteByIdDTO)}
        editEstimates={EditEstimate}
        isEditNotes={isEditNotes}
        onEstimatesDataUpdate={onEstimatesDataUpdate}
      />
      <DeleteDialog
        isOpen={deleteDialogControls.isOpen}
        onCancel={deleteDialogControls.close}
        onDeleteClick={() => deleteEstimate(deleteEstimateId)}
        target="Parameter based Estimate"
        title="De-activate Parameter based estimate"
        confirmTitle="De-activate"
      />
      <DeleteDialog
        isOpen={permanentDeleteDialogControls.isOpen}
        onCancel={permanentDeleteDialogControls.close}
        onDeleteClick={handlePermanentDeleteEstimate}
        target="Delete Estimate"
        title="Delete Parameter Based Estimate?"
      />
    </Box>
  );
};
